import { useState, useEffect, useCallback } from 'react';
import { getCalculationFormulas, getViableFormulas, ViableFormulasResponse } from '@/lib/api/clients';
import { CalculationFormula, DRVGuideline } from '@/types/calculation-formulas';
import { getCountryCode } from '@/utils/country-code-mapping';
import { resolveAllowedActivityLevels } from '@/utils/activity-levels';
import { calculateAgeFromDateString } from '../date-utils';

interface UseFormulasParams {
  dateOfBirth: string;
  gender: string;
  location: string;
  currentStep: number;
}

export const useFormulas = ({ dateOfBirth, gender, location, currentStep }: UseFormulasParams) => {
  const [allFormulas, setAllFormulas] = useState<CalculationFormula[]>([]);
  const [viableFormulas, setViableFormulas] = useState<CalculationFormula[]>([]);
  const [isLoadingFormulas, setIsLoadingFormulas] = useState(false);
  const [defaultFormulaId, setDefaultFormulaId] = useState<string | null>(null);
  const [drvGuidelines, setDrvGuidelines] = useState<DRVGuideline[]>([]);
  const [isLoadingDrvGuidelines, setIsLoadingDrvGuidelines] = useState(false);
  const [defaultDRVGuidelineId, setDefaultDRVGuidelineId] = useState<string | null>(null);
  const [allowedActivityLevels, setAllowedActivityLevels] = useState<ViableFormulasResponse['allowedActivityLevels']>(null);

  const fetchFormulas = useCallback(async () => {
    const calculatedAge = calculateAgeFromDateString(dateOfBirth) ?? 0;

    // Get country code from location
    const countryCode = location ? getCountryCode(location) : undefined;

    setIsLoadingFormulas(true);
    setIsLoadingDrvGuidelines(true);
    try {
      const response = await getViableFormulas({
        age: calculatedAge,
        gender: gender,
        countryCode: countryCode,
      });

      setAllFormulas(response.allFormulas || []);
      setViableFormulas(response.viableFormulas || []);
      setDefaultFormulaId(response.defaultFormulaId || null);
      setDrvGuidelines(response.availableDRVGuidelines || []);
      setDefaultDRVGuidelineId(response.defaultDRVGuidelineId || null);
      setAllowedActivityLevels(
        resolveAllowedActivityLevels({
          age: calculatedAge,
          biologicalSex: gender === 'male' || gender === 'female' ? gender : '',
          backendAllowedActivityLevels: response.allowedActivityLevels || null,
        })
      );
    } catch (error) {
      console.error('Error fetching viable formulas:', error);
      // Fallback to fetching all formulas
      try {
        const response = await getCalculationFormulas();
        const formulasArray = Array.isArray(response.formulas) ? response.formulas : [];
        setAllFormulas(formulasArray);
        setViableFormulas(formulasArray);
        setDrvGuidelines([]);
        setAllowedActivityLevels(
          resolveAllowedActivityLevels({
            age: calculatedAge,
            biologicalSex: gender === 'male' || gender === 'female' ? gender : '',
            backendAllowedActivityLevels: null,
          })
        );
      } catch (fallbackError) {
        console.error('Error fetching all formulas:', fallbackError);
        setAllFormulas([]);
        setViableFormulas([]);
        setDrvGuidelines([]);
        setAllowedActivityLevels(
          resolveAllowedActivityLevels({
            age: calculatedAge,
            biologicalSex: gender === 'male' || gender === 'female' ? gender : '',
            backendAllowedActivityLevels: null,
          })
        );
      }
    } finally {
      setIsLoadingFormulas(false);
      setIsLoadingDrvGuidelines(false);
    }
  }, [dateOfBirth, gender, location]);

  const reset = useCallback(() => {
    setAllFormulas([]);
    setViableFormulas([]);
    setDefaultFormulaId(null);
    setDrvGuidelines([]);
    setDefaultDRVGuidelineId(null);
    setAllowedActivityLevels(null);
  }, []);

  return {
    allFormulas,
    viableFormulas,
    isLoadingFormulas,
    defaultFormulaId,
    drvGuidelines,
    isLoadingDrvGuidelines,
    defaultDRVGuidelineId,
    allowedActivityLevels,
    fetchFormulas,
    reset,
  };
};
